/**
 * Google Analytics 4 — chargement dynamique + événements SPA.
 *
 * Variable de build : VUE_APP_GA4_MEASUREMENT_ID (ex. G-XXXXXXXXXX)
 * Debug local : localStorage.setItem('ga4_debug', '1') puis recharger.
 */

import { normalizeWizardStepId } from './wizardSteps';

const PENDING_PURCHASE_KEY = 'ga4_pending_purchase';
const DEBUG_FLAG = 'ga4_debug';

function getMeasurementId() {
  return String(
    typeof process !== 'undefined' && process.env && process.env.VUE_APP_GA4_MEASUREMENT_ID
      ? process.env.VUE_APP_GA4_MEASUREMENT_ID
      : ''
  ).trim();
}

function isLocalHost() {
  const host = String(typeof window !== 'undefined' ? window.location?.hostname : '').toLowerCase();
  return host === 'localhost' || host === '127.0.0.1';
}

function isDebugEnabled() {
  try {
    return window.localStorage?.getItem(DEBUG_FLAG) === '1';
  } catch (_) {
    return false;
  }
}

export function isAnalyticsEnabled() {
  return !!getMeasurementId() && (!isLocalHost() || isDebugEnabled());
}

let initialized = false;

export function initAnalytics() {
  if (typeof window === 'undefined' || initialized) return false;

  const measurementId = getMeasurementId();
  if (!measurementId) return false;
  if (isLocalHost() && !isDebugEnabled()) return false;

  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }

  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    send_page_view: false,
    anonymize_ip: true,
  });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(script);

  initialized = true;
  return true;
}

export function trackPageView({ path, title, location } = {}) {
  if (!isAnalyticsEnabled() || typeof window.gtag !== 'function') return;

  window.gtag('event', 'page_view', {
    page_path: path || `${window.location.pathname}${window.location.search}${window.location.hash}`,
    page_title: title || document.title,
    page_location: location || window.location.href,
    send_to: getMeasurementId(),
  });
}

export function trackEvent(eventName, params = {}) {
  if (!isAnalyticsEnabled() || typeof window.gtag !== 'function') return;
  window.gtag('event', eventName, params);
}

/** Formulaire contact public — événement GA4 recommandé generate_lead */
export function trackGenerateLead({ demande } = {}) {
  trackEvent('generate_lead', {
    lead_type: demande || 'contact',
    currency: 'XOF',
  });
}

/** Inscription validée (email vérifié) */
export function trackSignUp(method = 'email') {
  trackEvent('sign_up', { method });
}

/** Connexion réussie */
export function trackLogin(method = 'username') {
  trackEvent('login', { method });
}

/** Clic « payer » avant redirection vers le prestataire */
export function trackBeginCheckout({ offer, plan, value } = {}) {
  trackEvent('begin_checkout', {
    currency: 'XOF',
    value: value > 0 ? value : undefined,
    items: offer
      ? [{ item_id: offer, item_name: offer, item_category: plan || 'annual' }]
      : undefined,
  });

  try {
    sessionStorage.setItem(
      PENDING_PURCHASE_KEY,
      JSON.stringify({
        offer,
        plan,
        value: value > 0 ? value : undefined,
        currency: 'XOF',
        ts: Date.now(),
      })
    );
  } catch (_) {
    /* quota / mode privé */
  }
}

/** Page /paiement/succes — lit les données en attente si disponibles */
export function trackPurchaseFromPending() {
  let pending = null;
  try {
    const raw = sessionStorage.getItem(PENDING_PURCHASE_KEY);
    sessionStorage.removeItem(PENDING_PURCHASE_KEY);
    if (raw) pending = JSON.parse(raw);
  } catch (_) {
    /* ignore */
  }

  trackEvent('purchase', {
    currency: pending?.currency || 'XOF',
    value: pending?.value,
    items: pending?.offer
      ? [
          {
            item_id: pending.offer,
            item_name: pending.offer,
            item_category: pending.plan || 'annual',
            price: pending.value,
          },
        ]
      : undefined,
  });
}

/** Page /paiement/erreur */
export function trackPaymentFailed(reason = 'payment_cancelled') {
  try {
    sessionStorage.removeItem(PENDING_PURCHASE_KEY);
  } catch (_) {
    /* ignore */
  }
  trackEvent('payment_failed', { reason });
}

const MON_ANALYSE_FORM = 'mon_analyse';
const GA4_FORM_START_KEY = 'ga4_mon_analyse_started';

function withFormContext(params = {}) {
  return { form_name: MON_ANALYSE_FORM, ...params };
}

/** Ouverture du formulaire /mon-analyse (1× par session) */
export function trackMonAnalyseFormStart({ offer } = {}) {
  try {
    if (sessionStorage.getItem(GA4_FORM_START_KEY) === '1') return;
    sessionStorage.setItem(GA4_FORM_START_KEY, '1');
  } catch (_) {
    /* ignore */
  }
  trackEvent('form_start', withFormContext({ offer: offer || undefined }));
}

/** Choix Persona / Duo */
export function trackMonAnalyseSelectOffer(offer) {
  trackEvent('select_offer', withFormContext({ offer }));
}

/** Infos personnelles validées (KYC initial) */
export function trackMonAnalysePersonalInfoSubmit({ offer, ordreDuo } = {}) {
  trackEvent('personal_info_submit', withFormContext({
    offer: offer || undefined,
    ordre_duo: ordreDuo > 0 ? ordreDuo : undefined,
  }));
}

/** Écran attente validation KYC */
export function trackMonAnalyseKycWaitingView({ offer } = {}) {
  trackEvent('kyc_waiting_view', withFormContext({ offer: offer || undefined }));
}

/** Profil jugé apte — questionnaire débloqué */
export function trackMonAnalyseKycApproved({ offer } = {}) {
  trackEvent('kyc_approved', withFormContext({ offer: offer || undefined }));
}

/** Soumission du questionnaire */
export function trackMonAnalyseFormSubmit({ offer, ordreDuo } = {}) {
  trackEvent('form_submit', withFormContext({
    offer: offer || undefined,
    ordre_duo: ordreDuo > 0 ? ordreDuo : undefined,
  }));
}

/** Analyse enregistrée avec succès */
export function trackMonAnalyseComplete({ offer, analysisId, ordreDuo } = {}) {
  trackEvent('analysis_complete', withFormContext({
    offer: offer || undefined,
    analysis_id: analysisId != null ? String(analysisId) : undefined,
    ordre_duo: ordreDuo > 0 ? ordreDuo : undefined,
  }));
}

/**
 * Dernière section atteinte dans /mon-analyse, pour qualifier l'abandon.
 * L'étape du wizard fait foi ; les indicateurs de remplissage ne servent que
 * pour les sessions ouvertes avant l'introduction de `wizardStepId`.
 */
export function resolveMonAnalyseLastSection({
  isReportGenerated = false,
  showRestOfForm = false,
  objectif = null,
  pret_en_cours: pretEnCours = null,
  hasFinancialData = false,
  situation = null,
  isPersonalInfoLocked = false,
  clientApte = false,
  wizardStepId = null,
} = {}) {
  if (isReportGenerated) return 'submit';

  const step = normalizeWizardStepId(wizardStepId);
  if (step) return step;

  if (objectif) return 'page4_objectifs';
  if (pretEnCours) return 'page3b_passifs_revenus';
  if (hasFinancialData) return 'page3_finances';
  if (situation || showRestOfForm) return 'page2_situation';
  if (isPersonalInfoLocked && !clientApte) return 'kyc_wait';
  return 'page1_profile';
}

/** Abandon avec brouillon non soumis */
export function trackMonAnalyseFormAbandon({ lastSection, offer } = {}) {
  trackEvent('form_abandon', withFormContext({
    last_section: lastSection || undefined,
    offer: offer || undefined,
  }));
}

/** Page vues marketing (funnel public) */
export function trackViewHome() {
  trackEvent('view_home');
}
export function trackViewPlatform() {
  trackEvent('view_platform');
}
export function trackViewOffers() {
  trackEvent('view_offers');
}

/** Clic CTA marketing */
export function trackCtaClick({ location, label } = {}) {
  trackEvent('cta_click', {
    location: location || undefined,
    label: label || undefined,
  });
}

/** Funnel Diagnostic Patrimonial */
export function trackDiagnosticStart() {
  trackEvent('diagnostic_start');
}
export function trackDiagnosticStep(step) {
  trackEvent('diagnostic_step', { step });
}
export function trackDiagnosticComplete({ segment, priority, score } = {}) {
  trackEvent('diagnostic_complete', {
    segment: segment || undefined,
    priority: priority || undefined,
    score: score != null ? score : undefined,
  });
}
export function trackLeadSubmit({ segment, priority, source } = {}) {
  trackEvent('lead_submit', {
    segment: segment || undefined,
    priority: priority || undefined,
    source: source || 'diagnostic',
  });
  trackGenerateLead({ demande: source || 'diagnostic' });
}
export function trackBookingClick({ location } = {}) {
  trackEvent('booking_click', { location: location || 'diagnostic_result' });
}
